<template>
  <modal-dialog
    ref="dialog"
    :visible="dialog"
    persistent
    scrollable
    :max-width="contacts.length === 2 ? '60%' : '95%'"
    max-height="60%"
    container-class="pa-4"
    :title="$t('title.merge_contacts')"
    fluid
  >
    <v-row>
      <!-- all values same -->
      <v-col
        v-if="allOptionsSame"
        class="text-center"
        cols="12"
      >
        {{ $t('message.merge_contact_all_values_same') }}
      </v-col>
      <!-- some values differ -->
      <v-col
        v-else
        class="text-center"
        cols="12"
      >
        {{ $t('message.merge_contacts_values_different') }}
      </v-col>

      <v-col
        v-for="(c,i) in contacts"
        :key="i"
        class="text-center"
      >
        <span class="text-subtitle-1">{{ $t('label.contact') }} {{ i+1 }}</span>
      </v-col>
    </v-row>

    <contacts-merge-row
      v-for="fd in displayedFieldDescriptors.filter((fd) => !fd.readOnly)"
      :key="fd.field"
      :label="fd.label"
      :values="contacts.map((c)=>fieldValue(fd.field, c) || c[fd.field])"
      :selected-side="sideSelection.get(fd.field)"
      :type="fd.type"
      :read-only="fd.readOnly"
      @side-selected="sideSelected(fd.field, $event)"
    />
    <v-divider class="my-5" />
    <contacts-merge-row
      v-for="fd in displayedFieldDescriptors.filter((fd) => fd.readOnly)"
      :key="fd.field"
      :label="fd.label"
      :values="contacts.map((c)=>fieldValue(fd.field, c) || c[fd.field])"
      :selected-side="sideSelection.get(fd.field)"
      :type="fd.type"
      :read-only="fd.readOnly"
      @side-selected="sideSelected(fd.field, $event)"
    />

    <template #actions>
      <v-spacer />
      <v-btn
        :disabled="!allOptionsMerged && !allOptionsSame && contacts.length > 1"
        color="success"
        @click="save"
      >
        {{ $t('button.save') }}
      </v-btn>
      <v-btn
        text
        color="error"
        @click="cancel"
      >
        {{ $t('button.cancel') }}
      </v-btn>
    </template>
  </modal-dialog>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue';
import DialogClosable from '@/mixins/dialog-closable';
import { cloneModel } from '@/model/model-utils';
import Contact from '@/grpc-api/model/contact';
import ContactsMergeRow from './ContactsMergeRow.vue';

export default Vue.extend({
  name: 'ContactsMergeDialog',
  components: { ContactsMergeRow },
  mixins: [DialogClosable],
  props: {
    contacts: { type: Array as PropType<Array<Contact>>, required: true, default: () => [] },
  },
  data() {
    return {
      contact: new Contact(),
      sideSelection: new Map<string, number>(),
      fieldDescriptors: [
        { field: 'name', label: this.$i18n.tc('label.person_name'), type: 'string' },
        { field: 'companyName', label: this.$i18n.tc('label.company'), type: 'string' },
        { field: 'email', label: this.$i18n.tc('label.email'), type: 'string' },
        { field: 'phone', label: this.$i18n.tc('label.phone'), type: 'string' },

        { field: 'notes', label: this.$i18n.tc('label.note'), type: 'string' },
        { field: 'isVIP', label: this.$i18n.tc('label.vip'), type: 'boolean' },
        { field: 'title', label: this.$i18n.tc('label.person_title'), type: 'string' },
        { field: 'newsletterSubscription', label: this.$i18n.tc('label.newsletter_subscription'), type: 'boolean' },

        { field: 'address', label: this.$i18n.tc('label.address'), type: 'string' },
        { field: 'postalCode', label: this.$i18n.tc('label.zip_code'), type: 'string' },
        { field: 'state', label: this.$i18n.tc('label.state'), type: 'string' },
        { field: 'city', label: this.$i18n.tc('label.city'), type: 'string' },
        { field: 'country', label: this.$i18n.tc('label.country'), type: 'string' },
        { field: 'preferredLanguage', label: this.$i18n.tc('label.preferred_language'), type: 'string' },
        { field: 'dateOfBirth', label: this.$i18n.tc('label.birthday'), type: 'string' },

        {
          field: 'updated', label: this.$i18n.tc('label.updated'), type: 'date', readOnly: true,
        },
        {
          field: 'visitCount', label: this.$i18n.tc('label.visits'), type: 'number', readOnly: true,
        },
        {
          field: 'lastVisit', label: this.$i18n.tc('label.last_visit'), type: 'date', readOnly: true,
        },
      ],
    };
  },
  computed: {
    displayedFieldDescriptors(): ({ field: string, label: string, type: string, readOnly?: boolean })[] {
      const descriptors = this.fieldDescriptors.filter(
        (fd) => this.contacts.some((c: any) => c[fd.field] !== (this.contact as any)[fd.field] || fd.readOnly === true),
      );
      return descriptors;
    },
    allOptionsSame(): boolean {
      return this.displayedFieldDescriptors.length === 0;
    },
    allOptionsMerged(): boolean {
      return this.displayedFieldDescriptors.every(
        (fd) => this.sideSelection.get(fd.field) !== undefined || fd?.readOnly,
      );
    },
  },
  watch: {
  },
  mounted() {
    this.contact = cloneModel(this.contacts[0]);
    this.fieldDescriptors.forEach((fd) => {
      if (fd.readOnly) return;
      const index = this.contacts.findIndex(
        (c: any) => c[fd.field] !== undefined && c[fd.field] !== null && c[fd.field] !== '' && c[fd.field] !== 0,
      );
      if (index < 0) return;
      this.sideSelection.set(fd.field, index);
    });
  },
  methods: {
    cancel() {
      this.$emit('close');
    },
    save() {
      const contact = new Contact();
      this.fieldDescriptors.forEach((fd) => {
        if (fd.readOnly) return;
        const index = this.sideSelection.get(fd.field);
        if (index !== undefined) (contact as any)[fd.field] = (this.contacts[index] as any)[fd.field];
      });
      this.$emit('merge', contact);
    },
    sideSelected(field: string, event: { field: string, side: number }) {
      this.sideSelection.set(field, event.side);
      this.sideSelection = new Map(this.sideSelection);
      (this.contact as any)[field] = (this.contacts[event.side] as any)[field];
    },
    fieldValue(field: string, contact: Contact) {
      const fd = this.fieldDescriptors.find((o) => o.field === field);
      const value = (contact as any)[field];

      if (!fd || value === undefined || value === null) return value;

      if (value.text !== undefined) return value.text;

      switch (fd.type) {
        case 'date': return this.$localized.shortDateText(value as Date);
        case 'boolean': return value === true ? this.$i18n.tc('label.true') : this.$i18n.tc('label.false');
        default: return value;
      }
    },
  },
});
</script>

<style scoped>

</style>
